/**
 * DeviceList — Renders registered devices with add/remove controls.
 */

import { useState } from 'react';
import { Plus, Trash2, Monitor, Loader2 } from 'lucide-react';
import { AddDeviceModal } from './AddDeviceModal';

/**
 * Simple middle-truncation for fixed-length strings like UUIDs.
 * Pure string operation — no DOM measurement needed.
 */
function truncateDeviceCode(text, maxLen = 20) {
    if (!text || text.length <= maxLen) return text;
    const ellipsis = '…';
    const keep = maxLen - ellipsis.length;
    const prefixLen = Math.ceil(keep / 2);
    const suffixLen = Math.floor(keep / 2);
    return text.slice(0, prefixLen) + ellipsis + text.slice(text.length - suffixLen);
}

export function DeviceList({
    devices,
    maxDevices,
    onAddDevice,
    onRemoveDevice,
    isLoading,
    isRevoked,
    initialDeviceCode = '',
    autoOpenModal = false,
}) {
    const [modalOpen, setModalOpen] = useState(false);
    const [removingCode, setRemovingCode] = useState(null);

    const canAdd = devices.length < maxDevices && !isRevoked;

    // Auto-open AddDeviceModal when device_code comes from URL and device isn't already registered
    useState(() => {
        if (
            autoOpenModal &&
            initialDeviceCode &&
            canAdd &&
            !devices.some((d) => d.device_code === initialDeviceCode)
        ) {
            setModalOpen(true);
        }
    });

    async function handleAdd(deviceCode, deviceName) {
        try {
            await onAddDevice(deviceCode, deviceName);
            setModalOpen(false);
        } catch {
            // Error is surfaced via parent notification
        }
    }

    async function handleRemove(deviceCode) {
        if (!window.confirm('Remove this device? You can add a new one later.')) return;
        setRemovingCode(deviceCode);
        try {
            await onRemoveDevice(deviceCode);
        } catch {
            // Error surfaced via parent
        } finally {
            setRemovingCode(null);
        }
    }

    function formatDate(isoStr) {
        if (!isoStr) return '';
        return new Intl.DateTimeFormat(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(new Date(isoStr));
    }

    return (
        <section className="space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                    Devices ({devices.length}/{maxDevices})
                </h3>
                {canAdd && (
                    <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Add Device
                    </button>
                )}
            </div>

            {devices.length === 0 ? (
                <div className="text-center py-6 text-sm text-zinc-500 dark:text-zinc-400 border border-dashed border-zinc-300 dark:border-zinc-700 rounded-lg">
                    <Monitor className="w-8 h-8 mx-auto mb-2 text-zinc-300 dark:text-zinc-600" />
                    <p>No devices registered yet.</p>
                    {!isRevoked && (
                        <button
                            type="button"
                            onClick={() => setModalOpen(true)}
                            className="mt-2 text-indigo-600 dark:text-indigo-400 hover:underline text-xs font-medium"
                        >
                            Add your first device
                        </button>
                    )}
                </div>
            ) : (
                <ul className="divide-y divide-zinc-200 dark:divide-zinc-700 border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden">
                    {devices.map((device) => (
                        <li
                            key={device.device_code}
                            className="flex items-center justify-between px-4 py-3 bg-white dark:bg-zinc-900"
                        >
                            <div className="min-w-0">
                                <p className="text-sm font-mono text-zinc-800 dark:text-zinc-200 truncate">
                                    {truncateDeviceCode(device.device_code, 20)}
                                </p>
                                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                                    {device.device_name && (
                                        <span className="font-medium">{device.device_name} · </span>
                                    )}
                                    Added {formatDate(device.added_at)}
                                </p>
                            </div>
                            {!isRevoked && (
                                <button
                                    type="button"
                                    onClick={() => handleRemove(device.device_code)}
                                    disabled={removingCode === device.device_code}
                                    className="ml-3 p-1.5 rounded text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-colors"
                                    aria-label={`Remove device ${device.device_name || device.device_code}`}
                                >
                                    {removingCode === device.device_code ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Trash2 className="w-4 h-4" />
                                    )}
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            <AddDeviceModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleAdd}
                isLoading={isLoading}
                initialDeviceCode={initialDeviceCode}
            />
        </section>
    );
}
